import React, { useState, useEffect } from 'react'
import {
  FormGroup,
  EditableText,
  H2,
  Spinner,
  Intent,
  Classes,
  Tag,
  Switch,
  Divider,
  SpinnerSize,
} from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { IconButton, Toaster, Flex, Confirmation, Details, Button } from '@ui'
import { NavigateFn } from '@reach/router'
import { handleErrors } from './TableView'
import api, { graphQLError, ISpaceTypeWithCompany } from '@api'
import { InteractivityType } from '@api/space/spaceType'
import { omit } from 'lodash'
import useSpaceType from './hooks/useSpaceType'
import track from '@track'
import Event from '@track/Event'
import { Dialog } from '@ui'

export default function SpaceTypeDetails({
  spaceTypeId,
  navigate,
  reload,
}: {
  spaceTypeId: string
  navigate?: NavigateFn
  reload?: () => void
}) {
  const { spaceType, isLoading, updateSpaceType } = useSpaceType(spaceTypeId)
  const [value, setValue] = useState<null | string>(null)
  const [errors, setErrors] = useState<{ [id: string]: React.ReactNode }>({})
  const [updating, setUpdating] = useState(false)

  useEffect(() => console.log(errors), [errors])

  const onClose = () => {
    navigate?.(window.location.pathname)
  }

  const onRemove = () => {
    track.spaceTypeDialog({
      eventName: Event.DELETE_RECORD_DIALOG,
    })
    return api.space.spaceType
      .remove(spaceType.id)
      .then(() => {
        reload?.()
        Toaster.success({
          message: 'Space Type Removed',
        })
        onClose()
      })
      .catch((error: { graphQLErrors: graphQLError[] }) => {
        const stringErrors = handleErrors(error, false) as string[]
        stringErrors.forEach((e: string) => Toaster.fail({ message: e }))
      })
  }

  const onUpdate = ({
    interactivity,
    hidden,
    name,
  }: {
    interactivity?: InteractivityType
    hidden?: boolean
    name?: string
  }) => {
    setUpdating(true)
    api.space.spaceType
      .update({
        id: spaceTypeId,
        interactivity,
        hidden,
        name,
      })
      .then((updatedSpaceType: ISpaceTypeWithCompany) => {
        setUpdating(false)
        setErrors(omit(setErrors, spaceTypeId))
        reload?.()
        updateSpaceType(updatedSpaceType)
        setValue(null)
        Toaster.success({
          message: 'Space Type Updated',
        })
      })
      .catch((error: { graphQLErrors: graphQLError[] }) => {
        setUpdating(false)
        setValue(spaceType.displayName)
        setErrors({
          ...errors,
          [spaceTypeId]: handleErrors(error, true),
        })
      })
  }

  return (
    <Dialog
      isOpen
      title=""
      isCloseButtonShown={false}
      onClose={onClose}
      style={{ minWidth: 400 }}
      toolbar={
        <div className="dialog-buttons flex">
          {spaceType.displayName && !spaceType.company && (
            <>
              <Button
                minimal
                text={spaceType.hidden ? 'Unhide' : 'Hide'}
                onClick={() => onUpdate({ hidden: !spaceType.hidden })}
                loading={updating}
              />
              <Divider />
            </>
          )}

          {spaceType.company && (
            <Confirmation
              cancelable
              text={<div style={{ width: 200, whiteSpace: 'normal' }}>Delete this Space Type?</div>}
              onConfirm={onRemove}
            >
              <IconButton minimal className="remove-icon" tooltip="Delete" iconName="trash" />
            </Confirmation>
          )}

          <IconButton minimal iconName="times" onClick={onClose} tooltip="Close" />
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <H2 className={isLoading ? Classes.SKELETON : ''}>
            <FormGroup
              style={{
                marginBottom: 'none',
                marginRight: updating ? 20 : 0,
              }}
              helperText={errors[spaceTypeId]}
              intent={errors[spaceTypeId] ? Intent.DANGER : undefined}
            >
              <Flex align="center" justify="flex-start" style={{ width: '100%' }}>
                {spaceType.company ? (
                  <EditableText
                    multiline={false}
                    value={value || spaceType.displayName}
                    onChange={(newValue) => {
                      setValue(newValue)
                    }}
                    onConfirm={() => {
                      if (value && value !== spaceType.displayName) {
                        track.spaceTypeDialog({
                          eventName: Event.INLINE_ACTION,
                          props: {
                            actionName: 'Rename Space Type',
                            actionValue: null,
                          },
                        })

                        onUpdate({ name: value.trim() })
                      } else {
                        setValue(null)
                      }
                    }}
                  />
                ) : (
                  spaceType.displayName
                )}
                {updating && (
                  <div style={{ width: 20, marginLeft: 10 }}>
                    <Spinner size={SpinnerSize.SMALL} />
                  </div>
                )}
              </Flex>
            </FormGroup>
          </H2>
          {!spaceType.company ? (
            <Tooltip2 content="Default Space Types cannot be edited or removed">
              <Tag minimal className="ml-1 cursor-default">
                Default
              </Tag>
            </Tooltip2>
          ) : (
            <Details.Row>
              <Details.Label label="Seatable" required />
              <Details.Column>
                <Switch
                  disabled={updating}
                  checked={spaceType.interactivityType === 2}
                  onChange={() =>
                    onUpdate({
                      interactivity:
                        spaceType.interactivityType === 2
                          ? InteractivityType.ALLOCATABLE
                          : InteractivityType.SEATABLE,
                    })
                  }
                />
              </Details.Column>
            </Details.Row>
          )}
        </div>
      }
    />
  )
}
